import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, TextField, IconButton, Card, CardMedia, CircularProgress, Snackbar, Alert } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { PAGE_NAME, BUG_TYPE, STATUS } from '../../ticketing/Constants';

// Function to generate a random 6 to 8 digit unique ID
const generateUniqueId = () => {
  return Math.floor(100000 + Math.random() * 90000000).toString();
};

// Function to resize the image
const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height *= maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width *= maxHeight / height));
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(file.type));
      };
      img.src = event.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const BugReportDialog = ({ open, onClose, markerInfo, searchedAddress, arrivalDateTime, desiredParkingTime }) => {
  const [bugDescription, setBugDescription] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  //console.log('markerInfo', markerInfo, searchedAddress, arrivalDateTime, desiredParkingTime);


  // Handle file change to resize and store the selected files in state
  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    const resizedFiles = await Promise.all(files.map(file => resizeImage(file, 800, 800))); // Resize to 800x800 max
    const newScreenshots = resizedFiles.map((dataUrl, index) => ({
      fileName: files[index].name,
      data: dataUrl
    }));
    setScreenshots(prevScreenshots => [...prevScreenshots, ...newScreenshots]);
    setFileInputKey(Date.now()); // Reset file input to allow selecting the same file again if needed
  };

  // Remove a screenshot from the state
  const handleRemoveScreenshot = (index) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  // Handle sending bug report to the backend
  const handleSendBugReport = async () => {
    //console.log('click send');
    setLoading(true);
    //console.log('markerInfo', markerInfo, searchedAddress, arrivalDateTime, desiredParkingTime);

    const bugReport = {
      bugId: generateUniqueId(),
      description: bugDescription,
      placeId: markerInfo.placeId,
      bugReportDate: new Date().toLocaleString(),
      bugReporter: "Tester",
      searchedAddress: searchedAddress,
      searchedLatitude: markerInfo.latLng[0],
      searchedLongitude: markerInfo.latLng[1],
      arrivalDateTime: arrivalDateTime,
      desiredParkingTime: desiredParkingTime,
      screenshots: screenshots,
      // screenshots: screenshots.map(screenshot => ({
      //   fileName: screenshot.fileName,
      //   data: screenshot.data
      // })),
      bugType: BUG_TYPE.BUG,
      pageName: PAGE_NAME.INVENTORY_MANAGEMENT,
      status: STATUS.PENDING
    };
    //console.log('Bug report payload:', bugReport);

    try {
      await axios.post('https://ve502n7sng.execute-api.us-west-2.amazonaws.com/beta/report-bug', bugReport);
      setSnackbarSeverity('success');
      setSnackbarMessage('Bug report sent successfully!');
      setSnackbarOpen(true);
      setBugDescription('');
      setScreenshots([]);

      setTimeout(() => {
        onClose(); // Close the dialog after a short delay
      }, 1000);

    } catch (error) {
      console.error('Error sending bug report:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request data:', error.request);
      } else {
        console.error('Error message:', error.message);
      }

      // setSnackbarSeverity('error');
      // setSnackbarMessage('Failed to send bug report. Please try again later.');
      // setSnackbarOpen(true);
      // setTimeout(() => {
      //   onClose(); // Close the dialog after a short delay
      // }, 1000);

      if (open) {
        onClose();
      }
      
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} 
        sx={{ zIndex: 9999 }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">



      <DialogTitle>Report Bug</DialogTitle>

      <DialogContent>
        <DialogContentText>Place ID: {markerInfo.placeId}</DialogContentText>

        <DialogContentText>
          Please provide details about the bug you encountered.
        </DialogContentText>

        <TextField
          margin="dense"
          label="Description of a bug"
          type="text"
          fullWidth
          multiline
          rows={5}
          value={bugDescription}
          onChange={(e) => setBugDescription(e.target.value)}
          variant="outlined"
        />

        <DialogContentText style={{ marginTop: '20px' }}>
          Upload screenshots (optional):
        </DialogContentText>


        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
          {screenshots.map((screenshot, index) => (
            <Card key={index} style={{ maxWidth: 200, position: 'relative' }}>
              <CardMedia
                component="img"
                image={screenshot.data}
                title={screenshot.fileName}
              />
              <IconButton
                style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(255, 255, 255, 0.7)' }}
                onClick={() => handleRemoveScreenshot(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Card>
          ))}
          <input
            key={fileInputKey}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            id="upload-screenshots"
            onChange={handleFileChange}
          />
          <label htmlFor="upload-screenshots">
            <IconButton color="primary" component="span">
              <AddPhotoAlternateIcon style={{ fontSize: 40 }} />
            </IconButton>
          </label>
        </div>

      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} style={{ backgroundColor: '#f44336', color: 'white' }} disabled={loading}>
          Close
        </Button>
        <Button onClick={handleSendBugReport} style={{ backgroundColor: '#4caf50', color: 'white' }} disabled={loading}>
          {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : 'Send'}
        </Button>
      </DialogActions>

      
    </Dialog>
    </>
  );
};

export default BugReportDialog;
